import { defineStore } from "pinia";

// main is the name of the store. It is unique across your application
// and will appear in devtools
export const useCheckoutStore = defineStore("checkout", {
  // a function that returns a fresh state
  state: () => ({
    tab: 1,
    cartDetails: {
      appointment_date: "",
      appointment_time: "",
    },

    maxTestLists: 4,
    directPaymentDetails: {},
    manualPaymentDetails: {},
    testLists: [],
    dependentTests: [],
    isGuest: false,
    steps: [
      {
        id: 1,
        slug: "date_and_time",
        titleKey: "checkout.step.dateTime",
      },
      {
        id: 2,
        slug: "patient_details",
        titleKey: "checkout.step.customerDetail",
      },
      {
        id: 3,
        slug: "payment",
        titleKey: "checkout.step.payment",
      },
    ],
    directPaymentSteps: [
      {
        id: 1,
        slug: "patient_details",
        titleKey: "checkout.step.customerDetail",
      },
      {
        id: 2,
        slug: "payment",
        titleKey: "checkout.step.payment",
      },
    ],
    manualPaymentSteps: [
      {
        id: 1,
        slug: "payment",
        titleKey: "checkout.step.payment",
      },
      {
        id: 2,
        slug: "order_confirmation",
        titleKey: "manualPayment.step.orderConfirmation",
      },
    ],
    appointment_date: null,
    appointment_time: null,
    createOrderResponse: {},
    addressCompleteHasError: false,
    cardElement: null,
    stripeClientSecret: null,
    paymentType: "",
    start_point: "unknown",
  }),
  getters: {
    checkoutSession() {
      return useCookie("checkoutSession", {
        sameSite: true,
        secure: true,
      });
    },

    getCheckoutSession() {
      return this.checkoutSession.value;
    },

    translatedSteps(state) {
      return state.steps.map((step) => ({
        ...step,
        title: step.titleKey ? useTranslateStore(step.titleKey) : step.title,
      }));
    },
    translatedDirectPaymentSteps(state) {
      return state.directPaymentSteps.map((step) => ({
        ...step,
        title: step.titleKey ? useTranslateStore(step.titleKey) : step.title,
      }));
    },
    translatedManualPaymentSteps(state) {
      return state.manualPaymentSteps.map((step) => ({
        ...step,
        title: step.titleKey ? useTranslateStore(step.titleKey) : step.title,
      }));
    },
  },
  actions: {
    setCollection(item) {
      let isExisted = this.testLists.some((data) => data.id == item.id);
      if (isExisted) {
        this.testLists = this.testLists.filter((i) => i.id !== item.id);
        this.removeTestDependentChildren(item);
      } else {
        this.testLists = [...this.testLists, item];
      }
    },
    addTest(item) {
      let isExisted = this.testLists.some((data) => data.id == item.id);
      if (!isExisted) {
        this.testLists = [...this.testLists, item];
      }
    },
    removeTestLists() {
      this.testLists = [];
      this.dependentTests = [];
    },

    removeTestPanelLists(item) {
      this.testLists = this.testLists.filter((i) => i.id == item);
      this.removeTestDependentChildren(item);
    },
    addDependentTest(item) {
      let isExisted = this.dependentTests.some((data) => data.id == item.id);
      if (!isExisted) {
        this.dependentTests = [...this.dependentTests, item];
      }
    },
    removeDependentTest(item) {
      this.dependentTests = this.dependentTests.filter((data) => data.id !== item.id);
    },
    removeTestDependentChildren(item) {
      if (Array.isArray(item.children) && item.children.length > 0) {
        item.children.forEach((child) => {
          const existsInOtherTest = this.testLists.some(
            (test) => Array.isArray(test.children) && test.children.some((c) => c.id === child.id)
          );
          if (!existsInOtherTest) {
            this.removeDependentTest(child);
          }
        });
      }
    },
    checkAndAddDependentTest(item) {
      if (item.children && Array.isArray(item.children)) {
        const matchedDependents = item.children.filter((child) =>
          this.dependentTests.some((dep) => dep.id === child.id)
        );

        if (matchedDependents.length > 0) {
          matchedDependents.forEach((child) => {
            const existsInTestList = this.testLists.some((t) => t.id === child.id);
            if (!existsInTestList) {
              this.testLists = [...this.testLists, child];
            }
          });
        }
      }
    },
    nextStep() {
      this.navigate(this.tab + 1);
    },
    navigate(payload, navigationType = "push") {
      const steps =
        this.paymentType === "direct_payment"
          ? this.directPaymentSteps
          : this.paymentType === "manual_payment"
            ? this.manualPaymentSteps
            : this.steps;
      const nextStep = steps.find((step) => step.id === Number(payload));
      if (!nextStep) return;

      const router = useRouter();
      router[navigationType]({
        query: {
          step: nextStep.id,
        },
      });
      this.tab = Number(payload);
    },
    navigateAsGuest() {
      this.isGuest = true;
    },
    setAppointmentDate(value) {
      this.appointment_date = value;
      this.checkoutSession.value = {
        ...this.checkoutSession.value,
        appointment_date: value,
      };
    },
    setAppointmentTime(value) {
      this.appointment_time = value;
      this.checkoutSession.value = {
        ...this.checkoutSession.value,
        appointment_time: value,
      };
    },

    setSelectedClinicCookieData(clinicId, testId, startPoint) {
      this.checkoutSession.value = {
        clinic_id: clinicId,
        test_id: testId,
        start_point: startPoint,
        country_locale: useCookie("preferredCountry").value,
      };
    },
    resetCheckoutCookieId() {
      this.checkoutSession.value = null;
    },

    setSelectedClinicId(value) {
      this.checkoutSession.value = { clinic_id: value };
    },

    setCartDetails(value) {
      this.cartDetails = value;
    },
    setCreateOrderResponse(value) {
      this.createOrderResponse = value;
    },
    setAddressCompleteHasError(value) {
      this.addressCompleteHasError = value;
    },
    setCardElement(value) {
      this.cardElement = value;
    },
    setStripeClientSecret(value) {
      this.stripeClientSecret = value;
    },
    setDirectPaymentDetails(value) {
      this.directPaymentDetails = value;
    },
    setManualPaymentDetails(value) {
      this.manualPaymentDetails = value;
    },
    setPaymentType(value) {
      this.paymentType = value;
    },
  },
  persist: false,
});
